import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Input,
  InputDiv,
  SearchResultItemContent,
  SearchResultItemTitle,
  SearchResultItem,
  SearchResultWrapper,
  SearchWrapper,
  SearchResultItemsWrapper,
  PaginationWrapper,
  SearchResultText,
} from './atoms'
import { SearchModal } from './search-modal'
import { SearchIcon } from './svg/search'
import { CloseIcon } from './svg/close'
import { searchInstance, searchPageSize, SearchResultProps } from '../../service'
import { CmsContentService, debounce, Highlight } from '@foxrenderfarm/common'
import { Pagination } from '../pagination'
import { Breadcrumb } from '../breadcrumb'
import { Dir } from '../../interface'
import { findPath } from '../../utils'

interface Props {
  dirs: Dir[]
}

export const Search = ({ dirs }: Props) => {
  const [pageNum, setPageNum] = useState(1)
  const [total, setTotal] = useState(1)
  const [visible, setVisible] = useState(false)
  const searchRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [inputTop, setInputTop] = useState(0)
  const [inputLeft, setInputLeft] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [searchResult, setSearchResult] = useState<SearchResultProps['data']>([])

  const handleSearch = useCallback(async (value: string, page: number) => {
    if (!value) return
    const { data } = await searchInstance.searchNews(value, page)
    setSearchResult(data.data)
    setTotal(data.total)
  }, [])

  const debounceSearch = useRef(debounce(handleSearch, 300))

  const handleInputFocus = () => {
    setVisible(true)
    handleInputPosition()
  }

  const handleInputPosition = () => {
    if (searchRef.current) {
      setInputTop(
        searchRef.current.getBoundingClientRect().top < 0
          ? 0
          : searchRef.current.getBoundingClientRect().top,
      )
      setInputLeft(searchRef.current.getBoundingClientRect().left)
    }
  }

  const handleClose = () => {
    setVisible(false)
    setSearchValue('')
  }

  useEffect(() => {
    debounceSearch.current(searchValue, pageNum)
  }, [searchValue, pageNum])

  useEffect(() => {
    if (visible) {
      searchInputRef.current?.focus()
    }
  }, [visible])

  useEffect(() => {
    handleInputPosition()
    window.addEventListener('resize', handleInputPosition)
    return () => {
      window.removeEventListener('resize', handleInputPosition)
    }
  }, [])

  return (
    <>
      <SearchWrapper ref={searchRef} $background='rgba(255, 255, 255, 0.4)'>
        <SearchIcon className='search' />
        <InputDiv onClick={handleInputFocus}>Search</InputDiv>
      </SearchWrapper>
      <SearchModal
        visible={visible}
        handleMaskClick={() => setVisible(false)}
        top={inputTop}
        left={inputLeft}
      >
        <SearchWrapper $background='#ffffff'>
          <SearchIcon className='search' />
          <Input
            ref={searchInputRef}
            placeholder='Search'
            value={searchValue}
            onChange={(e) => {
              setPageNum(1)
              setSearchValue(e.target.value)
            }}
          />
          {searchValue && <CloseIcon className='close' onClick={handleClose} />}
        </SearchWrapper>
        {!!searchValue && (
          <SearchResultWrapper>
            <PaginationWrapper>
              <SearchResultText>
                Showing results {pageNum === 1 ? 1 : (pageNum - 1) * searchPageSize}–
                {pageNum * searchPageSize > total ? total : pageNum * searchPageSize} out of {total}{' '}
                for: <span className='value'>{searchValue}</span>
              </SearchResultText>
              <Pagination
                total={total}
                pageNum={pageNum}
                pageSize={searchPageSize}
                onChange={(v) => setPageNum(v)}
              />
            </PaginationWrapper>
            {searchResult.length > 0 && (
              <SearchResultItemsWrapper>
                {searchResult.map((item) => {
                  const prePath = findPath(dirs, item.slug)
                  return (
                    <SearchResultItem
                      key={item.slug}
                      to={`/support/${prePath?.map((item) => item.slug).join('/')}/${item.slug}`}
                    >
                      <Breadcrumb
                        dirs={dirs}
                        currentName={item.title}
                        currentSlug={item.slug}
                        hideHeadFooter
                        tight
                        noHref
                      />
                      <SearchResultItemTitle>
                        <Highlight keywords={searchValue} htmlFragment={item.title} />
                      </SearchResultItemTitle>
                      <SearchResultItemContent>
                        <Highlight
                          keywords={searchValue}
                          htmlFragment={CmsContentService.getAbstract(item.article)?.slice(0, 300)}
                        />
                      </SearchResultItemContent>
                    </SearchResultItem>
                  )
                })}
              </SearchResultItemsWrapper>
            )}
          </SearchResultWrapper>
        )}
      </SearchModal>
    </>
  )
}
